import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { buildApiUrl, API_CONFIG } from '../../utils/api';
import toast from 'react-hot-toast';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    corporationName: '',
    corporationSize: 5
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      const url = buildApiUrl(API_CONFIG.ENDPOINTS.REGISTER);
      
      const requestData = {
        name: formData.name,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        corporationName: formData.corporationName,
        corporationSize: parseInt(formData.corporationSize)
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok || response.status === 201) {
        setRegistrationSuccess(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || 'Error al crear la cuenta. Vuelve a intentar de nuevo');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error('Error al crear la cuenta. Vuelve a intentar de nuevo');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    navigate('/login');
  };

  // Si el registro fue exitoso, mostrar mensaje de confirmación
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="max-w-md w-full"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="flex justify-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="relative"
                >
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
                  </div>
                  <motion.div
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 1.8, opacity: 0 }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="absolute inset-0 bg-green-400 rounded-full"
                  />
                </motion.div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                ¡Cuenta Creada Exitosamente!
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Tu cuenta ha sido creada de forma exitosa.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-8">
                <p className="text-sm text-blue-900 dark:text-blue-200">
                  📧 Te hemos enviado un correo electrónico con un enlace para validar tu cuenta. 
                  Por favor revisa tu bandeja de entrada.
                </p>
              </div>
              <button
                onClick={handleFinish}
                className="w-full btn-primary py-3 text-lg font-semibold"
              >
                Finalizar
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Register Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">P</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TeamiFlow</h1>
              </div>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {theme === 'light' ? '🌙' : '☀️'}
              </button>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Crea tu cuenta</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Únete a miles de equipos que ya gestionan sus proyectos con TeamiFlow
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Nombre *
                  </label>
                  <div className="mt-1">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="given-name"
                      required
                      className="input-field"
                      placeholder="Ej: Juan"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Apellido *
                  </label>
                  <div className="mt-1">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      required
                      className="input-field"
                      placeholder="Ej: Pérez"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Correo electrónico *
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="input-field"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="corporationName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nombre de la Corporación *
                </label>
                <div className="mt-1">
                  <input
                    id="corporationName"
                    name="corporationName"
                    type="text"
                    required
                    className="input-field"
                    placeholder="Ej: Bitto Corp"
                    value={formData.corporationName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="corporationSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tamaño de la Corporación *
                </label>
                <div className="mt-1">
                  <select
                    id="corporationSize"
                    name="corporationSize"
                    required
                    className="input-field"
                    value={formData.corporationSize}
                    onChange={handleChange}
                  >
                    <option value="5">1-5 empleados</option>
                    <option value="10">6-10 empleados</option>
                    <option value="20">11-20 empleados</option>
                    <option value="50">21-50 empleados</option>
                    <option value="100">51-100 empleados</option>
                    <option value="200">101-200 empleados</option>
                    <option value="500">201-500 empleados</option>
                    <option value="1000">501-1000 empleados</option>
                    <option value="5000">1001-5000 empleados</option>
                    <option value="10000">Más de 5000 empleados</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Contraseña
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="input-field pr-10"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confirmar contraseña
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="input-field pr-10"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="terms" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Acepto los{' '}
                  <button className="text-primary-600 hover:text-primary-500">
                    términos de servicio
                  </button>{' '}
                  y la{' '}
                  <button className="text-primary-600 hover:text-primary-500">
                    política de privacidad
                  </button>
                </label>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                </button>
              </div>
            </form>

            {/* Mensaje de prueba gratis */}
            <div className="mt-8 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
              <div className="flex items-start space-x-3">
                <span className="text-2xl">🎁</span>
                <div className="flex-1">
                  <div className="text-center mb-3">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      ¡Comienza gratis por 30 días!
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Explora todas las funciones sin compromiso
                    </p>
                  </div>
                  <div className="text-center mb-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      No se requiere tarjeta.
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Al finalizar la prueba podrás elegir un plan.
                    </p>
                  </div>
                  <div className="text-center">
                    <Link 
                      to="/plans" 
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 inline-flex items-center"
                    >
                      Ver planes y precios →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Link para iniciar sesión */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ¿Ya tienes una cuenta?{' '}
                <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 inline-flex items-center">
                  Iniciar sesión
                  <span className="ml-1">→</span>
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Hero Image/Content */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-600 via-primary-600 to-secondary-800">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative h-full flex flex-col justify-center items-center text-white p-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center mb-8 mx-auto backdrop-blur-sm">
                <span className="text-4xl">🎯</span>
              </div>
              <h2 className="text-4xl font-bold mb-4">Comienza tu viaje hacia la excelencia</h2>
              <p className="text-xl text-white/90 mb-8 max-w-md">
                Únete a equipos que han transformado su productividad con metodologías ágiles y herramientas modernas.
              </p>
              <div className="grid grid-cols-2 gap-6 text-left max-w-md">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-sm">🏆</span>
                  </div>
                  <span className="text-sm">Mejores prácticas</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-sm">🔄</span>
                  </div>
                  <span className="text-sm">Flujos adaptativos</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-sm">📈</span>
                  </div>
                  <span className="text-sm">Crecimiento continuo</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-sm">🤝</span>
                  </div>
                  <span className="text-sm">Colaboración efectiva</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
